import { createRootRouteWithContext, Outlet, Link } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Header } from '@widgets/header';
import { Footer } from '@widgets/footer';

interface RouterContext {
  queryClient: QueryClient;
}

/**
 * 404-Seite im Ultra-Fan-Stil für die Web-App.
 * Dramatischer dunkler Gradient-Hintergrund, animierter Scan-Line-Effekt,
 * großes Gradient-"404" und CTA zurück zur Startseite.
 */
function NotFoundPage() {
  return (
    <>
      <style>{`
        @keyframes notFoundFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes notFoundSlideIn {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes notFoundGradientShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes notFoundScanLine {
          from { transform: translateY(-100%); }
          to   { transform: translateY(100vh); }
        }
        @keyframes notFoundGlow {
          0%, 100% { text-shadow: 0 0 40px rgba(184,78,72,0.4), 0 0 80px rgba(184,78,72,0.2); }
          50%      { text-shadow: 0 0 60px rgba(184,78,72,0.7), 0 0 120px rgba(184,78,72,0.35); }
        }
        @keyframes notFoundPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.6; }
        }

        .nf-bg {
          background: linear-gradient(160deg, #1d1d1b 0%, #2c1a19 50%, #1d1d1b 100%);
          background-size: 200% 200%;
          animation: notFoundGradientShift 12s ease infinite;
        }
        .nf-scan-line {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }
        .nf-scan-line::after {
          content: '';
          position: absolute;
          left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(184,78,72,0.5), transparent);
          animation: notFoundScanLine 4s linear infinite;
          opacity: 0.6;
        }
        .nf-404 {
          background: linear-gradient(135deg, #ffffff 0%, #c96b66 55%, #b84e48 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: notFoundGlow 3s ease-in-out infinite, notFoundFadeUp 0.8s ease-out forwards;
          opacity: 0;
        }
        .nf-eyebrow {
          animation: notFoundSlideIn 0.6s ease-out 0.2s forwards;
          opacity: 0;
        }
        .nf-headline {
          animation: notFoundFadeUp 0.7s ease-out 0.4s forwards;
          opacity: 0;
        }
        .nf-body {
          animation: notFoundFadeUp 0.7s ease-out 0.55s forwards;
          opacity: 0;
        }
        .nf-cta {
          animation: notFoundFadeUp 0.7s ease-out 0.7s forwards;
          opacity: 0;
        }
        .nf-divider {
          animation: notFoundFadeUp 0.6s ease-out 0.85s forwards;
          opacity: 0;
        }
        .nf-btn {
          position: relative;
          overflow: hidden;
          transition: background-color 0.2s ease, transform 0.15s ease;
        }
        .nf-btn:hover {
          transform: translateY(-2px);
        }
        .nf-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0.06);
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .nf-btn:hover::after {
          opacity: 1;
        }
        .nf-logo-pulse {
          animation: notFoundPulse 4s ease-in-out infinite;
        }
      `}</style>

      <div className="nf-bg relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 py-20 text-center">
        {/* Halftone-Textur */}
        <div className="pointer-events-none absolute inset-0 texture-halftone" aria-hidden="true" />

        {/* Scan-Line */}
        <div className="nf-scan-line" aria-hidden="true" />

        {/* Vertikale Akzent-Linie links */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1 bg-accent opacity-70"
          aria-hidden="true"
        />

        {/* Dekorativer Hintergrundtext */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
          aria-hidden="true"
        >
          <span className="select-none text-[clamp(120px,28vw,320px)] font-black uppercase tracking-widest text-white/[0.018] leading-none">
            ERROR
          </span>
        </div>

        <div className="relative z-10 flex flex-col items-center gap-6">
          {/* Fan-Logo */}
          <div className="nf-logo-pulse mb-2">
            <img
              src="/logos/fan-logo-white.svg"
              alt="Faninitiative Spandau"
              className="h-14 w-auto opacity-60"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>

          {/* Eyebrow */}
          <div className="nf-eyebrow flex items-center gap-3">
            <span className="h-px w-8 bg-accent" aria-hidden="true" />
            <span className="text-xs font-black uppercase tracking-[0.3em] text-accent">
              Faninitiative Spandau e.V.
            </span>
            <span className="h-px w-8 bg-accent" aria-hidden="true" />
          </div>

          {/* 404 */}
          <h1
            className="nf-404 font-black leading-none tracking-tight"
            style={{ fontSize: 'clamp(6rem, 22vw, 16rem)' }}
            aria-label="404 — Seite nicht gefunden"
          >
            404
          </h1>

          {/* Überschrift */}
          <p className="nf-headline text-[clamp(1.25rem,3vw,2rem)] font-black uppercase tracking-widest text-white">
            Seite nicht gefunden
          </p>

          {/* Beschreibung */}
          <p className="nf-body max-w-md text-sm leading-relaxed text-white/50 sm:text-base">
            Diese Seite existiert nicht. Vielleicht wurde sie verschoben, umbenannt oder ist nie
            erschienen — wie ein Auswärtssieg in Überzahl.
          </p>

          {/* Trennlinie */}
          <div className="nf-divider flex w-full max-w-xs items-center gap-4">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] font-black uppercase tracking-widest text-white/20">
              Zurück ins Spiel
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {/* CTA */}
          <div className="nf-cta">
            <Link
              to="/"
              className="nf-btn inline-flex items-center gap-2 bg-accent px-8 py-3 text-xs font-black uppercase tracking-widest text-white no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M9.293 2.293a1 1 0 011.414 0l7 7A1 1 0 0117 11h-1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1H9a1 1 0 00-1 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-6H3a1 1 0 01-.707-1.707l7-7z"
                  clipRule="evenodd"
                />
              </svg>
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Root-Route mit globalem Layout.
 * Enthalt Header, Hauptinhalt (Outlet) und Footer.
 * Die notFoundComponent greift bei allen unbekannten Routen.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  ),
  notFoundComponent: NotFoundPage,
});
